import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';

const RUN_ON_INIT = 'INITIALIZATION';
const RUN_ON_EACH_PAGE = 'EACH_PAGE';

/**
 * CodeSnippets
 * Accepts a list of code snippets with or without script tags:
 *
 * <script>console.log('i am the javascript');</script>
 *
 * console.log('i am the javascript');
 *
 * "initialization" snippets only added once
 * "each page" snippets run on page changes and need to be removed
 * and re-added to the page for re-execution
 *
 * cvent-script-id chosen as an identifier so we can query all
 * for removal when a snippet contains multiple script tags
 * (can't use id since they would need to be unique)
 *
 */

const htmlToElements = htmlString => {
  const template = document.createElement('template');
  template.innerHTML = htmlString.trim();
  return template.content.children;
};

const getExistingTagsForScript = ({ id }) => {
  return document.querySelectorAll(`[cvent-script-id="${id}"]`);
};

const addSnippetToPage = ({ id, codeSnippetValue }) => {
  // Check for html comments in snippet, they make it hard to parse
  const trimmedValue = codeSnippetValue.replace(/<!--[\s\S]*?-->/g, '').trim();

  if (trimmedValue.indexOf('<script') === 0) {
    // snippets in html format

    // Create list of elements from html
    // so we can parse each script's innerHTML (the actual js)
    const elements = htmlToElements(trimmedValue);
    for (let i = 0; i < elements.length; i++) {
      const scriptEl = elements.item(i);
      const elAttributes = scriptEl.attributes;

      if (scriptEl.nodeName === 'SCRIPT') {
        const newScript = document.createElement('script');
        // Re-apply attributes to preserve original script tag attributes
        for (let j = 0; j < elAttributes.length; j++) {
          newScript.setAttribute(
            elAttributes.item(j).name,
            elAttributes.item(j).value
          );
        }

        newScript.setAttribute('cvent-script-id', id);
        newScript.appendChild(document.createTextNode(scriptEl.innerHTML));

        document.body.appendChild(newScript);
      } else {
        // Other types are added too, for example tracking pixels
        // fb tracking pixel example here:
        // https://wiki.cvent.com/pages/viewpage.action?spaceKey=PM&title=Flex+Code+Snippets#FlexCodeSnippets-CodeSnippetExamples
        scriptEl.setAttribute('cvent-script-id', id);
        document.body.appendChild(scriptEl);
      }
    }
  } else {
    // plain js snippets
    const newScript = document.createElement('script');
    newScript.setAttribute('cvent-script-id', id);
    newScript.appendChild(document.createTextNode(trimmedValue));
    document.body.appendChild(newScript);
  }
};

const addSnippetsToPage = snippets => {
  snippets.forEach(snippet => {
    const existingTags = getExistingTagsForScript(snippet);
    if (existingTags.length === 0) {
      addSnippetToPage(snippet);
    }
  });
};

const addSnippetsOnPageChange = snippets => {
  snippets
    .filter(({ applicableOn }) => {
      return applicableOn === RUN_ON_EACH_PAGE;
    })
    .forEach(snippet => {
      const existingTags = getExistingTagsForScript(snippet);
      if (existingTags.length > 0) {
        existingTags.forEach(tag => {
          tag.remove();
        });
      }

      addSnippetToPage(snippet);
    });
};

const CodeSnippets = ({ snippets = [] }) => {
  const router = useRouter();

  // At init add all snippets to page and run.
  useEffect(() => {
    addSnippetsToPage(snippets);
  }, [snippets]);

  // Re-add all RUN_ON_EACH_PAGE snippets to page and run.
  useEffect(() => {
    const handleRouteChange = () => {
      addSnippetsOnPageChange(snippets);
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router, snippets]);

  return null;
};

CodeSnippets.propTypes = {
  snippets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      dataTagCode: PropTypes.string,
      applicableOn: PropTypes.oneOf([RUN_ON_INIT, RUN_ON_EACH_PAGE]),
      codeSnippetValue: PropTypes.string,
      canDisableCodeSnippet: PropTypes.bool
    })
  )
};

export default CodeSnippets;
